import { Component, forwardRef, Input, OnInit, SimpleChanges, input, output, viewChild } from "@angular/core";
import { NgForm, NgModel, NG_VALUE_ACCESSOR, FormsModule } from "@angular/forms";
import { SelectItem, SharedModule } from "primeng/api";
import { Observable, takeUntil } from "rxjs";
import { Lot } from "../../../types";
import { PCPLotSelectorBaseComponent } from "../pcp-lot-selector.basecomponent";
import { HasValuePipe } from "../../../pipes/hasvalue.pipe";
import { ErrormessageComponent } from "../../validation/errormessage/errormessage.component";
import { CheckboxModule } from "primeng/checkbox";
import { FluidModule } from "primeng/fluid";

import { MultiSelectModule } from "primeng/multiselect";
import { Button } from "primeng/button";


@Component({
    selector: "pcp-lot-multiselect",
    templateUrl: "./pcp-lot-multi-select.component.html",
    styleUrls: [],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => PCPLotMultiSelectComponent),
        }
    ],
    standalone: true,
    imports: [Button, MultiSelectModule, FormsModule, SharedModule, CheckboxModule, FluidModule, ErrormessageComponent, HasValuePipe]
})
export class PCPLotMultiSelectComponent extends PCPLotSelectorBaseComponent implements OnInit {
    readonly lotSelection = viewChild<NgModel>("lotSelection");

    // TODO: Skipped for migration because:
    //  Accessor inputs cannot be migrated as they are too complex.
    @Input() set lots(lots: Lot[]) {
        if (!lots) {
            return;
        }

        this._lots = lots;
        this.lotOptions = this.lotsToOptions(lots);
    }

    get lots(): Lot[] {
        return this._lots;
    }

    _lots: Lot[] = [];


    _observable: Observable<Lot[]>;

    // TODO: Skipped for migration because:
    //  Accessor inputs cannot be migrated as they are too complex.
    @Input() set lotsObservable(observable: Observable<Lot[]>) {
        this._observable = observable;
        this.reloadOptions();
    };

    get lotsObservable(): Observable<Lot[]> {
        return this._observable;
    }

    readonly appendTo = input<string>(undefined);
    readonly placeholder = input<string>("Lot Block(s)");
    readonly maxSelectedLabels = input<number>(5);
    readonly showClearSelectionsButton = input<boolean>(false);
    readonly showToggleAll = input<boolean>(true);
    readonly showIncludeElevationSwitch = input<boolean>(false);
    readonly includeElevationSwitchDisabled = input<boolean>(false);

    // Two-way binding for selectedLots
    // TODO: Skipped for migration because:
    //  Accessor inputs cannot be migrated as they are too complex.
    @Input() set selectedLots(lots: Lot[]) {
        this._selectedLots = lots;
        this.propagateChange(this._selectedLots);
    };

    get selectedLots(): Lot[] {
        return this._selectedLots;
    }

    _selectedLots: Lot[];

    readonly selectedLotsChange = output<Lot[]>();

    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    @Input() includeElevation: boolean = false;
    readonly includeElevationChange = output<boolean>();

    readonly lotsLoaded = output<any>();

    get overrideHeader(): boolean {
        return this.showIncludeElevationSwitch() || this.showClearSelectionsButton();
    }

    lotOptions: SelectItem[];

    get label(): string {
        if (this.isLoading) return this.loadingText;

        return this.placeholder();
    }

    constructor() {
        super();
    }

    ngOnInit() {
        const selectionLimit = this.selectionLimit();
        if (selectionLimit && selectionLimit <= 0) { throw new TypeError("PCPLotMultiSelectComponent: 'selectionLimit' must be left null for no limit or set greater than 0"); }

        this.lotOptions = this.lotsToOptions(this._lots);
    }

    writeValue(obj: any) {
        this.selectedLots = obj;
    }

    isSingleSelect(): boolean {
        return this.selectionLimit() === 1;
    }

    onChange() {
        const sortedLots = this.sortSelectedTop(this._lots, this.selectedLots);
        this.lotOptions = this.lotsToOptions(sortedLots);
        this.selectedLotsChange.emit(this.selectedLots);
    }

    onIncludeElevationChange() {
        this.includeElevationChange.emit(this.includeElevation);
    }

    selectLotById(lotId: number) {
        const selectedLot = this._lots.find((lot: Lot) => lot.lotId === lotId);
        if (!selectedLot) { throw new TypeError(`PCPLotMultiSelectComponent.selectLotById(${lotId}) : lot not found.`); }

        if (this.isSingleSelect()) {
            this.clearSelectedLots();
        }

        this.selectLot(selectedLot);
    }

    clearSelectedLots(): void {
        this.selectedLots = [];
    }

    reloadOptions(): void {
        if (!this.lotsObservable) { return; }

        this.isLoading = true;
        this.lotsObservable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe((lots) => {
                this.lots = lots;
                this.lotOptions = this.lotsToOptions(lots);
                if (this.selectedLots) {
                    this.selectedLots = this.selectedLots.filter(selectedLot => lots.some(lot => lot.id === selectedLot.id));
                    this.selectedLotsChange.emit(this.selectedLots);
                }

                this.isLoading = false;
                // TODO: The 'emit' function requires a mandatory any argument
                this.lotsLoaded.emit(null);
            });
    }

    clearOptions() {
        this.lots = [];
        this.lotOptions = [];
    }

    allLotsAreSelected(): boolean {
        return this.selectedLots && this.lots && this.selectedLots.length === this.lots.length;
    }

    hasLots(): boolean {
        return this.lots && this.lots.length > 0;
    }

    private selectLot(lot: Lot) {
        this.selectedLots.push(lot);
    }
}
